import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import recetaService, { RecetaListItemDto } from '../../services/recetaService';

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 350;

interface Props {
  label?: string;
  value: string;
  descripcionHint?: string;
  /** Si ya hay receta resuelta, no busca hasta que el usuario edite. */
  recetaResuelta?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  onChange: (value: string) => void;
  onSelect: (item: RecetaListItemDto) => void;
  onResolveExact: (codigo: string) => void;
  onOpenDialog: () => void;
}

const RecetaCodigoLookupField: React.FC<Props> = ({
  label = 'Receta',
  value,
  descripcionHint,
  recetaResuelta = false,
  disabled,
  error,
  helperText,
  onChange,
  onSelect,
  onResolveExact,
  onOpenDialog,
}) => {
  const [suggestions, setSuggestions] = useState<RecetaListItemDto[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);
  const anchorRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const selectingRef = useRef(false);

  const resetSuggestions = useCallback(() => {
    setSuggestions([]);
    setOpen(false);
    setHighlightedIndex(-1);
    itemRefs.current = [];
  }, []);

  const buscar = useCallback(
    async (q: string) => {
      const trimmed = q.trim();
      if (!trimmed || disabled || recetaResuelta) {
        resetSuggestions();
        return;
      }

      const requestId = ++searchRequestIdRef.current;
      setLoading(true);
      try {
        const res = await recetaService.getRecetas({
          searchTerm: trimmed,
          pageNumber: 1,
          pageSize: PAGE_SIZE,
        });
        if (requestId !== searchRequestIdRef.current) return;

        const items = res.items ?? [];
        setSuggestions(items);
        setOpen(items.length > 0);
        setHighlightedIndex(items.length > 0 ? 0 : -1);
        itemRefs.current = [];
      } catch {
        if (requestId !== searchRequestIdRef.current) return;
        resetSuggestions();
      } finally {
        if (requestId === searchRequestIdRef.current) setLoading(false);
      }
    },
    [disabled, recetaResuelta, resetSuggestions],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (recetaResuelta) {
      resetSuggestions();
      return;
    }
    debounceRef.current = setTimeout(() => void buscar(value), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, recetaResuelta, buscar, resetSuggestions]);

  useEffect(() => {
    if (highlightedIndex >= 0) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleSelect = (item: RecetaListItemDto) => {
    selectingRef.current = true;
    searchRequestIdRef.current += 1;
    resetSuggestions();
    onSelect(item);
    setTimeout(() => {
      selectingRef.current = false;
    }, 0);
  };

  const handleBlur = () => {
    if (selectingRef.current || disabled || recetaResuelta) return;
    setOpen(false);
    const trimmed = value.trim();
    if (trimmed) onResolveExact(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === 'F4' || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'l')) {
      e.preventDefault();
      resetSuggestions();
      onOpenDialog();
      return;
    }

    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setHighlightedIndex(-1);
      }
      return;
    }

    if (open && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          if (prev < 0) return 0;
          return prev < suggestions.length - 1 ? prev + 1 : 0;
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          if (prev < 0) return suggestions.length - 1;
          return prev > 0 ? prev - 1 : suggestions.length - 1;
        });
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
        const item = suggestions[idx];
        if (item) handleSelect(item);
        return;
      }
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      resetSuggestions();
      if (!recetaResuelta) onResolveExact((e.target as HTMLInputElement).value);
    }
  };

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (open) setOpen(false);
      }}
    >
      <Box ref={anchorRef} sx={{ position: 'relative', width: '100%' }}>
        <TextField
          label={label}
          size="medium"
          fullWidth
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setOpen(true);
          }}
          error={error}
          helperText={helperText || (descripcionHint ? descripcionHint : ' ')}
          disabled={disabled}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <IconButton
                    size="small"
                    edge="end"
                    disabled={disabled}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      resetSuggestions();
                      onOpenDialog();
                    }}
                    aria-label="Buscar receta"
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={{ '& input': { fontFamily: 'monospace' } }}
        />

        <Popper
          open={open && suggestions.length > 0}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1400 }}
        >
          <Paper
            elevation={6}
            sx={{
              mt: 0.5,
              minWidth: Math.max(anchorRef.current?.clientWidth ?? 0, 380),
              maxWidth: 520,
              maxHeight: 280,
              overflowY: 'auto',
              borderRadius: 2,
            }}
          >
            <List dense disablePadding>
              {suggestions.map((s, idx) => (
                <ListItem key={s.codigoReceta} disablePadding divider>
                  <ListItemButton
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                    }}
                    selected={idx === highlightedIndex}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(s)}
                    sx={{ py: 0.75, px: 1.5, alignItems: 'flex-start' }}
                  >
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" noWrap>
                        {s.descripcion}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.25} flexWrap="wrap">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {s.codigoReceta}
                        </Typography>
                        {s.codigoProductoTerminado ? (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            PT: {s.codigoProductoTerminado}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default RecetaCodigoLookupField;
